import { use, useCallback, useEffect, useRef, useState } from 'react';

import type { Step } from '@/components/Flow/types';
import { allSteps } from '@/components/Step/registry';
import { WizardContext } from '@/components/Wizard';
import { FlowListContext } from '@/components/Wizard/FlowList';
import { trackStepView } from '@/lib/gtm';
import useRegistryStore from '@/stores/registry';

export type StepEnhanced = Step & {
  index: number;
  arrayIndex: number;
};

const toProgressIndex = (steps: Step[], arrayIndex: number): number => {
  const step = steps[arrayIndex];

  if (!step) {
    return 0;
  }

  const endExclusive = step.untraceable ? arrayIndex + 1 : arrayIndex;
  return steps.slice(0, endExclusive).filter(s => !s.untraceable).length;
};

const findInitialStep = (steps: Step[], data: Record<string, unknown>) => {
  const skippedNames: string[] = [];

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const { validator } = allSteps[step.name];

    if (validator && validator.safeParse(data).success) {
      skippedNames.push(step.name);
      continue;
    }

    return {
      step: { ...step, arrayIndex: i, index: toProgressIndex(steps, i) },
      skippedNames
    };
  }

  return { step: undefined, skippedNames };
};

const findAdjacentNextStep = (steps: Step[], currentIndex: number): StepEnhanced | undefined => {
  const i = currentIndex + 1;

  if (i >= steps.length) {
    return undefined;
  }

  const step = steps[i];
  return { ...step, arrayIndex: i, index: toProgressIndex(steps, i) };
};

const findPreviousStep = (steps: Step[], currentIndex: number) => {
  for (let i = currentIndex - 1; i >= 0; i--) {
    const step = steps[i];

    if (step.untraceable) {
      continue;
    }

    return { ...step, arrayIndex: i, index: toProgressIndex(steps, i) };
  }

  return undefined;
};

export const useSteps = (steps: Step[]) => {
  const stepData = useRegistryStore(state => state.registry);
  const { nextFlow, previousFlow } = use(FlowListContext);
  const { markStepVisited, currentFlowList, flowlistHistory } = use(WizardContext);

  const [skippedSteps] = useState<string[]>(() => findInitialStep(steps, stepData).skippedNames);

  const [currentStep, setCurrentStep] = useState<StepEnhanced | undefined>(() => findInitialStep(steps, stepData).step);

  const trackingRef = useRef({
    currentFlowList,
    flowlistHistory,
    skippedSteps,
    legalEntityRelation: stepData.legalEntityRelation
  });

  useEffect(() => {
    trackingRef.current = {
      currentFlowList,
      flowlistHistory,
      skippedSteps,
      legalEntityRelation: stepData.legalEntityRelation
    };
  }, [currentFlowList, flowlistHistory, skippedSteps, stepData.legalEntityRelation]);

  const handleVisit = useCallback(
    (visitedStep: Step) => {
      if (visitedStep.untraceable) {
        return;
      }

      markStepVisited(visitedStep.name);
    },
    [markStepVisited]
  );

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    handleVisit(currentStep);

    const { currentFlowList, flowlistHistory, skippedSteps, legalEntityRelation } = trackingRef.current;

    trackStepView(currentStep.name, currentFlowList, flowlistHistory, {
      flowIndex: currentStep.index,
      isSkipped: skippedSteps.includes(currentStep.name),
      registry: { legalEntityRelation }
    });
  }, [currentStep, handleVisit]);

  useEffect(() => {
    for (const name of skippedSteps) {
      const visitedStep = steps.find(step => step.name === name);

      if (!visitedStep) {
        continue;
      }

      handleVisit(visitedStep);
    }
  }, [steps, skippedSteps, handleVisit]);

  const nextStep = useCallback(() => {
    const step = findAdjacentNextStep(steps, currentStep?.arrayIndex ?? -1);

    if (!step) {
      nextFlow();
      return;
    }

    setCurrentStep(step);
  }, [steps, currentStep, nextFlow]);

  const previousStep = useCallback(() => {
    const step = findPreviousStep(steps, currentStep?.arrayIndex ?? -1);

    if (!step) {
      previousFlow();
      return;
    }

    setCurrentStep(step);
  }, [steps, currentStep, previousFlow]);

  return {
    steps,
    currentStep,
    nextStep,
    previousStep
  };
};
